import { CustomAlertApi } from '@agilelab/plugin-wb-platform';
import { Subscription } from '@backstage/types';
import { FieldExtensionUiSchema } from '../extensions/types';
import { ScaffolderApi } from '../types';
import { ReducerLogEntry } from './hooks/useEventStream';

// since "ui:style" as a uiSchema key has become protected in rjsf5, we remap it to this key instead so that it is not removed from uiSchema by rjsf
export const UI_STYLE_SAFE_KEY = 'ui:wb-style';

// TableRowTemplate deletes the original title and description from the schema to avoid rendering them in the table row
// This key is added to uiSchema in TableRowTemplate and can be used by pickers to show the original title of the field even when rendered inside the table row template
export const UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY =
  'ui:TableRowTemplateOriginalTitle';

export function extractCustomProperties<T>(
  uiSchema: FieldExtensionUiSchema<T, any>,
) {
  const customProps: any = {};
  const customStyle = uiSchema && uiSchema[UI_STYLE_SAFE_KEY];
  if (customStyle) {
    customProps.InputProps = {
      style: customStyle,
    };
  }
  return customProps;
}

export function isHidden(uiSchema: any): boolean {
  const isHiddenWidget = (s: any) =>
    s?.['ui:widget'] ? s['ui:widget'] === 'hidden' : false;
  // checks for legacy reasons if the ui:widget property is inside ui:options as well(deprecated, don't do that)
  const isLegacyHidden = (s: any) =>
    (s?.['ui:options'] as any)?.['ui:widget']
      ? (s['ui:options'] as any)?.['ui:widget'] === 'hidden'
      : false;
  return isHiddenWidget(uiSchema) || isLegacyHidden(uiSchema);
}

type SimplifiedTaskStream = {
  error?: Error;
  completed: boolean;
  failed: boolean;
};

/**
 * Finalizes the tasks and triggers the alertApi.
 * The whole alertApi object needs to be passed or it will break
 */
export const scaffolderTasksFinalizer = (
  taskIds: string[],
  successAlertMessage: string,
  scaffolderApi: ScaffolderApi,
  alertApi: CustomAlertApi,
) => {
  if (!taskIds) {
    return;
  }
  const completionRecord: Map<string, SimplifiedTaskStream> = new Map();
  taskIds.forEach(taskId => {
    completionRecord.set(taskId, {
      completed: false,
      failed: false,
    });
  });

  let finalized = false;

  const finalize = () => {
    if (
      !finalized &&
      Array.from(completionRecord.values()).every(
        taskStream => taskStream.completed,
      )
    ) {
      const failed = Array.from(completionRecord.values()).some(
        taskStream => taskStream.failed,
      );
      if (failed) {
        const error = failed
          ? Array.from(completionRecord.values()).find(
              taskStream => taskStream.failed,
            )?.error
          : undefined;
        alertApi.post({ error, severity: 'error' });
      } else {
        alertApi.post({ message: successAlertMessage, severity: 'success' });
      }
      finalized = true;
    }
  };

  const subscriptions: Record<string, Subscription> = {};

  taskIds.forEach(taskId => {
    const observable = scaffolderApi.streamLogs({ taskId });
    subscriptions[taskId] = observable.subscribe({
      next: event => {
        switch (event.type) {
          case 'completion':
            completionRecord.set(taskId, {
              completed: true,
              failed: (event as ReducerLogEntry).body.error ? true : false,
              error: (event as ReducerLogEntry).body.error,
            });
            finalize();
            return;
          default:
            return;
        }
      },
      error: error => {
        completionRecord.set(taskId, {
          completed: true,
          failed: true,
          error,
        });
        finalize();
      },
    });
  });
};
